import React from "react";
import { Handle, Position, type NodeProps } from "reactflow";
import { FiTool } from "react-icons/fi";

import { SchemaTableName } from "./SchemaTableName";
import type { ManualNodeData } from "./types";
import { NotGeneratedBadge, notGeneratedNodeClasses } from "./notGeneratedStyling";

/**
 * Manual / business-data-vault entity.
 *
 * These are not generated as a standard hub/sat/link (legacy `table_construct = MANUAL`).
 * They must never render as a hub — a hub with no business keys breaks hub/link logic —
 * so they get a distinct amber node. Source handles only: they can feed downstream
 * (e.g. data-mart) mappings, but they are not a business-key anchor.
 */
export function ManualNode({ data, selected }: NodeProps<ManualNodeData>): JSX.Element {
  const { entity, onSelect } = data;
  const baseClasses =
    "tw-group tw-relative tw-min-w-[200px] tw-cursor-grab tw-rounded-3xl tw-border tw-bg-gradient-to-br tw-from-white tw-via-slate-50 tw-to-slate-200 tw-p-5 tw-text-left tw-shadow-lg tw-transition-transform tw-duration-150 hover:tw--translate-y-1";
  const selectionClasses = selected
    ? "tw-border-[4px] tw-border-slate-700 tw-ring-[5px] tw-ring-slate-500/70 tw-shadow-[0_0_20px_rgba(51,65,85,0.5)]"
    : "tw-border-slate-300 tw-ring-1 tw-ring-slate-300/60";
  return (
    <div
      onClick={(event) => {
        if (event.shiftKey || event.metaKey || event.ctrlKey) {
          return;
        }
        onSelect(entity.id);
      }}
      className={`${baseClasses} ${selectionClasses} ${notGeneratedNodeClasses(entity.record)}`}
    >
      <NotGeneratedBadge record={entity.record} />
      <Handle
        type="source"
        id="manual-source-left"
        position={Position.Left}
        className="!tw-h-3 !tw-w-3 !tw-border-2 !tw-border-white !tw-bg-slate-400"
      />
      <Handle
        type="source"
        id="manual-source-right"
        position={Position.Right}
        className="!tw-h-3 !tw-w-3 !tw-border-2 !tw-border-white !tw-bg-slate-400"
      />
      <div className="tw-absolute tw-right-4 tw-top-4 tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-white/60 tw-bg-white/90 tw-px-2 tw-py-1 tw-text-[10px] tw-font-semibold tw-text-slate-600">
        <FiTool className="tw-h-3 tw-w-3" />
        <span>Manual</span>
      </div>
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-slate-500/90 tw-text-white tw-shadow-lg">
          <span className="tw-text-base tw-font-semibold">M</span>
        </div>
        <div>
          <p className="tw-text-sm tw-font-semibold tw-text-slate-800">Manual</p>
          <p className="tw-text-[11px] tw-uppercase tw-tracking-wide tw-text-slate-500">
            Business data vault
          </p>
        </div>
      </div>
      <div className="tw-mt-3">
        <SchemaTableName
          schema={entity.record.target_schema}
          table={entity.record.target_table}
          className="tw-block tw-font-mono tw-text-[13px] tw-font-semibold tw-leading-5 tw-text-slate-900 tw-break-words"
        />
      </div>
    </div>
  );
}
